<template>
    <section
        id="course-content"
        class="mt-8 mb-28 scroll-mt-24 space-y-6 sm:mb-8"
        aria-labelledby="course-content-title"
    >
        <div class="rounded-xl border border-default bg-surface-card p-5 sm:p-6">
            <div class="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h2 id="course-content-title" class="text-2xl font-semibold text-txt-headings">
                        Tvoj kurz, tvoje tempo
                    </h2>
                    <div class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-txt-body">
                        <span>{{ lessons.length }} lekcií</span>
                        <span v-if="duration">{{ durationLabel }}</span>
                        <span v-if="levelLabel">{{ levelLabel }}</span>
                    </div>
                </div>
                <CButton :aria-label="startLabel" variant="primary" @click="resumeCourse">{{ startLabel }}</CButton>
            </div>
            <div v-if="skill.learningOutcomes?.length" class="mt-6">
                <h3 class="font-semibold text-txt-headings">Čo sa naučíš</h3>
                <ul class="mt-3 grid gap-3 sm:grid-cols-2">
                    <li
                        v-for="(outcome, index) in skill.learningOutcomes"
                        :key="index"
                        class="flex items-start gap-2 text-sm text-txt-body"
                    >
                        <Icon
                            name="heroicons:check-circle"
                            class="mt-0.5 size-5 shrink-0 text-primary-600 dark:text-primary-300"
                        />
                        <span>{{ outcome }}</span>
                    </li>
                </ul>
            </div>
            <p v-if="skill.coursePrerequisites" class="mt-4 whitespace-pre-line text-sm text-txt-body">
                <strong>Pred začiatkom: </strong>{{ skill.coursePrerequisites }}
            </p>
        </div>

        <div class="grid items-start gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
            <aside
                class="min-w-0 rounded-xl border border-default bg-surface-card p-4"
                aria-label="Obsah a pokrok kurzu"
            >
                <h3 class="font-semibold text-txt-headings">Obsah kurzu</h3>
                <div class="my-4 space-y-2">
                    <div
                        class="flex items-center justify-between gap-2 text-sm text-txt-body"
                        role="status"
                        aria-live="polite"
                    >
                        <span>{{ completedCount }} z {{ lessons.length }} dokončených</span>
                        <span>{{ completionPercent }} %</span>
                    </div>
                    <progress
                        class="course-progress h-2 w-full overflow-hidden rounded-full"
                        :value="completedCount"
                        :max="lessons.length"
                        aria-label="Pokrok v kurze"
                    />
                    <p class="text-xs text-txt-body">
                        {{
                            storageAvailable
                                ? 'Pokrok sa ukladá iba v tomto prehliadači.'
                                : 'Pokrok sa teraz nedá uložiť. Zostane dostupný počas tejto návštevy.'
                        }}
                    </p>
                </div>
                <ol class="space-y-1">
                    <li v-for="(lesson, index) in lessons" :key="lesson.key">
                        <button
                            type="button"
                            class="flex w-full items-start gap-3 rounded-lg p-3 text-left transition-colors focus-visible:outline-2 focus-visible:outline-primary-500"
                            :class="
                                progress.activeLessonId === lesson.key
                                    ? 'bg-primary-50 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200'
                                    : 'text-txt-body hover:bg-subtle'
                            "
                            :aria-current="progress.activeLessonId === lesson.key ? 'step' : undefined"
                            :aria-label="`${index + 1}. ${lesson.title}${isCompleted(lesson.key) ? ', dokončená' : ''}`"
                            @click="selectLesson(lesson.key)"
                        >
                            <Icon
                                v-if="isCompleted(lesson.key)"
                                name="heroicons:check-circle"
                                class="mt-0.5 size-5 shrink-0 text-green-600 dark:text-green-400"
                            />
                            <span
                                v-else
                                class="flex size-6 shrink-0 items-center justify-center rounded-full border border-current text-xs"
                                >{{ index + 1 }}</span
                            >
                            <span class="min-w-0 break-words">
                                <span class="block text-sm font-medium">{{ lesson.title }}</span>
                                <span v-if="lesson.durationMinutes" class="mt-1 block text-xs"
                                    >{{ lesson.durationMinutes }} min</span
                                >
                            </span>
                        </button>
                    </li>
                </ol>
            </aside>

            <article
                v-if="activeLesson"
                class="min-w-0 space-y-5 rounded-xl border border-default bg-surface-card p-5 sm:p-6"
            >
                <div>
                    <p class="mb-2 text-sm text-txt-body">Lekcia {{ activeIndex + 1 }} z {{ lessons.length }}</p>
                    <h3
                        ref="lessonHeading"
                        tabindex="-1"
                        class="break-words text-2xl font-semibold text-txt-headings focus:outline-none"
                    >
                        {{ activeLesson.title }}
                    </h3>
                    <p v-if="activeLesson.desc" class="mt-3 text-txt-body">{{ activeLesson.desc }}</p>
                </div>
                <CEmbedVideo v-if="videoId" :key="activeLesson.key" :video-id="videoId" :title="activeLesson.title" />
                <div v-if="lessonHtml" class="wysiwyg-text course-lesson-content text-txt-body" v-html="lessonHtml" />
                <div v-if="activeLesson.exercise" class="rounded-lg bg-subtle p-4">
                    <h4 class="font-semibold text-txt-headings">Vyskúšaj si to</h4>
                    <p class="mt-2 whitespace-pre-line text-sm text-txt-body">{{ activeLesson.exercise }}</p>
                </div>
                <div class="flex flex-wrap items-center justify-between gap-3 border-t border-default pt-5">
                    <CButton
                        :aria-label="
                            isCompleted(activeLesson.key)
                                ? 'Označiť lekciu ako nedokončenú'
                                : 'Označiť lekciu ako dokončenú'
                        "
                        :aria-pressed="isCompleted(activeLesson.key)"
                        :variant="isCompleted(activeLesson.key) ? 'primary-outline' : 'primary'"
                        @click="toggleCompleted(activeLesson.key)"
                        >{{
                            isCompleted(activeLesson.key) ? 'Dokončená · vrátiť späť' : 'Označiť ako dokončené'
                        }}</CButton
                    >
                    <div class="flex flex-wrap gap-2">
                        <CButton
                            v-if="activeIndex > 0"
                            aria-label="Predchádzajúca lekcia"
                            variant="default"
                            @click="selectLesson(lessons[activeIndex - 1]!.key)"
                            >Späť</CButton
                        >
                        <CButton
                            v-if="activeIndex < lessons.length - 1"
                            aria-label="Ďalšia lekcia"
                            variant="primary-outline"
                            @click="selectLesson(lessons[activeIndex + 1]!.key)"
                            >Ďalšia lekcia</CButton
                        >
                    </div>
                </div>
                <p
                    v-if="completedCount === lessons.length"
                    role="status"
                    class="rounded-lg bg-green-50 p-4 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-300"
                >
                    Prešiel/prešla si všetky lekcie. Vráť sa k nim kedykoľvek alebo si zopakuj praktické cvičenia.
                </p>
            </article>

            <div v-else class="rounded-xl border border-default bg-surface-card p-6 sm:p-8">
                <Icon name="heroicons:book-open" class="mb-4 size-8 text-primary-600 dark:text-primary-300" />
                <h3 class="text-xl font-semibold text-txt-headings">Začni tým, čo sa chceš naučiť</h3>
                <p class="mt-3 text-txt-body">
                    Vyber si lekciu z obsahu alebo začni od prvej. Každú môžeš otvoriť znova a svoj pokrok označuješ ty.
                </p>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import type { Skill } from '#shared/types/Skill'
import { courseLevels, getCourseDuration, getLessonId, getLessonVideoId, hasLessonContent } from '#shared/utils/course'
import { sanitizeBlogHtml } from '#shared/utils/blog-html'
import { useCourseProgress } from '@/composables/skills/useCourseProgress'

const props = defineProps<{ skill: Skill }>()
const lessons = computed(() =>
    (props.skill.lessons || [])
        .map((lesson, index) => ({ ...lesson, key: getLessonId(lesson, index) }))
        .filter((lesson) => lesson.title?.trim() && hasLessonContent(lesson))
)
const lessonIds = computed(() => lessons.value.map((lesson) => lesson.key))
const { progress, storageAvailable, openLesson, toggleCompleted } = useCourseProgress(
    computed(() => props.skill.id),
    lessonIds
)
const activeIndex = computed(() => lessons.value.findIndex((lesson) => lesson.key === progress.value.activeLessonId))
const activeLesson = computed(() => lessons.value[activeIndex.value])
const lessonHtml = computed(() => sanitizeBlogHtml(activeLesson.value?.content || ''))
const videoId = computed(() => getLessonVideoId(activeLesson.value?.videoUrl))
const completedCount = computed(() => progress.value.completedLessonIds.length)
const completionPercent = computed(() =>
    lessons.value.length ? Math.round((completedCount.value / lessons.value.length) * 100) : 0
)
const duration = computed(() => getCourseDuration(lessons.value))
const durationLabel = computed(
    () => `${lessons.value.every((lesson) => !!lesson.durationMinutes) ? 'Približne' : 'Aspoň'} ${duration.value} min`
)
const levelLabel = computed(() => courseLevels.find((level) => level.value === props.skill.courseLevel)?.label)
const lessonHeading = ref<HTMLElement | null>(null)
const startLabel = computed(() => (progress.value.activeLessonId ? 'Pokračovať v kurze' : 'Začať kurz'))

function isCompleted(id: string) {
    return progress.value.completedLessonIds.includes(id)
}
async function selectLesson(id: string) {
    openLesson(id)
    await nextTick()
    lessonHeading.value?.focus({ preventScroll: true })
    lessonHeading.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
function resumeCourse() {
    const id =
        progress.value.activeLessonId ||
        lessons.value.find((lesson) => !isCompleted(lesson.key))?.key ||
        lessons.value[0]?.key
    if (id) void selectLesson(id)
}
</script>

<style scoped>
.course-progress {
    appearance: none;
    background: var(--color-gray-200);
}
.course-progress::-webkit-progress-bar {
    background: var(--color-gray-200);
    border-radius: 9999px;
}
.course-progress::-webkit-progress-value {
    background: var(--color-primary-500);
    border-radius: 9999px;
}
.course-progress::-moz-progress-bar {
    background: var(--color-primary-500);
    border-radius: 9999px;
}
.course-lesson-content {
    overflow-wrap: anywhere;
}
.course-lesson-content :deep(pre) {
    max-width: 100%;
    overflow-x: auto;
    white-space: pre-wrap;
    padding: 1rem;
    border-radius: 0.5rem;
    background: color-mix(in srgb, currentColor 5%, transparent);
}
.course-lesson-content :deep(table) {
    display: block;
    max-width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
}
.course-lesson-content :deep(th),
.course-lesson-content :deep(td) {
    border: 1px solid color-mix(in srgb, currentColor 20%, transparent);
    padding: 0.5rem;
    min-width: 7rem;
    text-align: left;
}
.course-lesson-content :deep(img),
.course-lesson-content :deep(iframe) {
    max-width: 100%;
}
</style>
